import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Sparkles, Heart, Star } from "lucide-react";
import { AuthContext } from "../context/AuthContext.jsx";

const InputField = ({ 
  type = "text", 
  name, 
  placeholder, 
  value, 
  onChange, 
  error, 
  showPasswordToggle = false,
  showPassword,
  setShowPassword,
  onKeyDown,
  tabIndex
}) => (
  <div className="mb-4">
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <div className={`w-2 h-2 rounded-full ${name === "username" ? "bg-blue-500" : name === "email" ? "bg-green-500" : "bg-purple-500"}`} />
      </div>
      <input
        type={showPasswordToggle && showPassword ? "text" : type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        className={`block w-full pl-10 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-300 ${
          error 
            ? "border-red-300 focus:border-red-500 focus:ring-red-200" 
            : "border-gray-300 focus:border-purple-500 focus:ring-purple-200"
        } bg-white/80 backdrop-blur-sm`}
      />
      {showPasswordToggle && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      )}
    </div>
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

const Login = () => {
  const { login, user, loading: authLoading, error: authError } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: ""
  });
  const [errors, setErrors] = useState({});
  const [apiMessage, setApiMessage] = useState("");
  const [floatingElements, setFloatingElements] = useState([]);

  // API configuration
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

  // ✅ CRITICAL FIX: Clear kiosk session on mount
  useEffect(() => {
    // Clear kiosk tokens to prevent cross-contamination
    localStorage.removeItem('kioskAuthToken');
    localStorage.removeItem('kioskUser');
    localStorage.removeItem('authToken'); // Legacy key
    localStorage.removeItem('user'); // Legacy key
    localStorage.removeItem('token'); // Another legacy key
    
    console.log('Admin login mounted - kiosk session cleared');
  }, []);

  // Function to convert technical error messages to user-friendly ones
  const getUserFriendlyMessage = (message) => {
    if (!message) return "";
    
    const lowerMessage = message.toLowerCase();
    
    // Handle token-related errors
    if (lowerMessage.includes('invalid token') || 
        lowerMessage.includes('token expired') || 
        lowerMessage.includes('no token') ||
        lowerMessage.includes('unauthorized')) {
      return "Please log in to continue";
    }
    
    // Handle authentication errors
    if (lowerMessage.includes('invalid credentials') || 
        lowerMessage.includes('wrong password') ||
        lowerMessage.includes('user not found')) {
      return "Invalid username or password. Please try again.";
    }
    
    // Handle network errors
    if (lowerMessage.includes('network error') || 
        lowerMessage.includes('fetch') ||
        lowerMessage.includes('connection')) {
      return "Connection error. Please check your internet connection and try again.";
    }
    
    // Handle server errors
    if (lowerMessage.includes('server error') || 
        lowerMessage.includes('500') ||
        lowerMessage.includes('503')) {
      return "Server is temporarily unavailable. Please try again later.";
    }
    
    // Return original message if no specific pattern matches
    return message;
  };

  useEffect(() => {
    // ✅ FIXED: Only redirect admin users, block non-admins
    console.log("Login useEffect - User:", user, "AuthLoading:", authLoading);
    if (user && !authLoading) {
      console.log("Redirecting based on role:", user.role);
      if (user.role === "admin") {
        navigate("/dashboard", { replace: true });
      } else if (user.role === "accountant") {
        navigate("/accounting", { replace: true });
      } else {
        navigate("/kiosk/login", { replace: true });
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Create floating elements for background animation
    const elements = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 3 + Math.random() * 2
    }));
    setFloatingElements(elements);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    if (apiMessage || authError) {
      setApiMessage("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username) {
      newErrors.username = "Username is required";
    }
    
    if (!isLogin && !formData.email) {
      newErrors.email = "Email is required";
    } else if (!isLogin && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!isLogin && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (registerData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: registerData.username,
          email: registerData.email,
          password: registerData.password,
          role: "admin",
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Registration failed");
      }

      const data = await response.json();
      setApiMessage(data.message || "Registration request sent. Please wait for admin approval.");
      setFormData({ username: "", email: "", password: "" });
      setIsLogin(true);
    } catch (error) {
      throw new Error(error.message || "Failed to connect to server");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setApiMessage("");

    try {
      if (isLogin) {
        console.log("Calling login with:", { username: formData.username, isKiosk: false });
        const response = await login(formData.username, formData.password, false); // ✅ false = admin login
        console.log("Login response:", response);
        
        // Validate this is an admin-side role
        if (response.user && !['admin', 'accountant'].includes(response.user.role)) {
          throw new Error("This login is for administrators only. Staff should use the Kiosk login.");
        }
        
        // Navigation will be handled by the useEffect above
      } else {
        await handleRegister(formData);
      }
    } catch (error) {
      console.error("Authentication error:", error);
      setApiMessage(getUserFriendlyMessage(error.message));
    } finally {
      setLoading(false);
    }
  };

  // Get user-friendly error message
  const displayMessage = getUserFriendlyMessage(apiMessage || authError);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600">
      <div className="absolute inset-0">
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className="absolute w-4 h-4 bg-white/10 rounded-full"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              animation: `float ${element.duration}s ease-in-out infinite ${element.delay}s alternate`
            }}
          />
        ))}
      </div>

      <div className="absolute top-20 left-10 text-white/20">
        <Sparkles className="w-8 h-8" />
      </div>
      <div className="absolute top-40 right-20 text-white/20">
        <Heart className="w-6 h-6" />
      </div>
      <div className="absolute bottom-20 left-20 text-white/20">
        <Star className="w-5 h-5" />
      </div>
      <div className="absolute top-60 right-10 text-white/20">
        <Sparkles className="w-4 h-4" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src={`${API_BASE_URL}/logo.png`}
              alt="Ellas Place Logo"
              className="mx-auto w-20 h-20 rounded-full mb-4 shadow-lg"
              onError={(e) => {
                e.target.src = "/fallback-logo.png";
              }}
            />
            <h1 className="text-3xl font-bold text-white mb-2">Ellas Place</h1>
            <p className="text-white/80 text-sm">Salon • Spa • Ladies Gym</p>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-300 to-pink-300 rounded-full mx-auto mt-3"></div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl p-8 border border-white/20">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {isLogin ? "Welcome Back" : "Create Admin Account"}
              </h2>
              <p className="text-gray-600 text-sm">
                {isLogin 
                  ? "Sign in to manage your business" 
                  : "Set up your admin account to manage Ellas Place"
                }
              </p>
            </div>

            {displayMessage && (
              <div className={`mb-4 p-3 rounded-lg ${
                displayMessage.includes('Registration request') 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-blue-50 border border-blue-200'
              }`}>
                <p className={`text-sm ${
                  displayMessage.includes('Registration request') 
                    ? 'text-green-600' 
                    : 'text-blue-600'
                }`}>
                  {displayMessage}
                </p>
              </div>
            )}

            <div className="space-y-4">
              <InputField
                name="username"
                placeholder="Enter your username"
                value={formData.username}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                error={errors.username}
                tabIndex={1}
              />

              {!isLogin && (
                <InputField
                  type="email"
                  name="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  error={errors.email}
                  tabIndex={2}
                />
              )}

              <InputField
                type="password"
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                error={errors.password}
                showPasswordToggle={true}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                tabIndex={isLogin ? 2 : 3}
              />

              {isLogin && (
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" tabIndex={3} />
                    <span className="ml-2 text-gray-600">Remember me</span>
                  </label>
                  <button type="button" className="text-purple-600 hover:text-purple-700 font-medium" tabIndex={4}>
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || authLoading}
                tabIndex={isLogin ? 5 : 4}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              >
                {loading || authLoading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    {isLogin ? "Sign In" : "Submit Registration"}
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </>
                )}
              </button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setFormData({ username: "", email: "", password: "" });
                    setErrors({});
                    setApiMessage("");
                  }}
                  tabIndex={isLogin ? 6 : 5}
                  className="ml-1 text-purple-600 hover:text-purple-700 font-semibold hover:underline transition-colors duration-300"
                >
                  {isLogin ? "Register here" : "Sign in here"}
                </button>
              </p>
            </div>

            {/* ✅ Kiosk Login Link - Added */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                Staff member?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/kiosk/login')}
                  className="text-purple-600 hover:text-purple-700 font-medium underline"
                  disabled={loading}
                >
                  Login to Kiosk POS
                </button>
              </p>
            </div>

            {!isLogin && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center mb-3">What you'll get:</p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center text-gray-600">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                    Complete Dashboard
                  </div>
                  <div className="flex items-center text-gray-600">
                    <div className="w-2 h-2 bg-pink-500 rounded-full mr-2"></div>
                    POS System
                  </div>
                  <div className="flex items-center text-gray-600">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    Inventory Management
                  </div>
                  <div className="flex items-center text-gray-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    Staff & Payroll
                  </div>
                </div>
              </div>
            )}
          </form>

          <div className="text-center mt-8">
            <p className="text-white/60 text-xs">
              © 2025 Ellas Place. All rights reserved. Powered by{" "}
              <a 
                href="https://opsuite.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white font-medium underline"
              >
                Opsuite.io
              </a>{" "}
              <a 
                href="https://wa.me/2349052463435" 
                className="text-white/60 hover:text-white/80"
              >
                09052463435
              </a>
            </p>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(180deg); }
            100% { transform: translateY(0px) rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Login;